<template>
  <multiselect
    :model-value="getValue"
    v-bind="options"
    variant="narrow"
    :searchable="true"
    @update:model-value="setValue"
  />
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import Multiselect from '@components/utils/Multiselect.vue';
import { computed } from 'vue';
import type { Variant } from '@components/utils/inputs/Field.vue';

export interface Option {
  labelProp?: string;
  variant?: Variant;
  label: string;
  value: string | null;
}

export type OptionsQueryFn = (query: string) => Promise<Option[]>;

export interface Options {
  options: Option[] | OptionsQueryFn;
  label: string;
  mode: string;
  valueSetter?: (model: Record<string, unknown>, key: string, value: string | null) => void;
  valueGetter?: (model: Record<string, unknown>, key: string) => void;
}

export interface Props<Value = never> {
  modelValue: Record<string, Value>;
  filterKey: 'dropdown';
  options: Options;
}

const emit = defineEmits(['update:modelValue']);
const props = defineProps<Props>();
const value = useVModel(props, 'modelValue', emit, { passive: true });

const getValue = computed(() => {
  const getter = props.options?.valueGetter ?? ((m, k) => m[k]);

  return getter(value.value, props.filterKey);
});

const setValue = (incomingValue: string | null) => {
  const setter = props.options?.valueSetter
    ?? ((m, k, v) => {
      m[k] = v;
    });

  setter(value.value, props.filterKey, incomingValue);
};
</script>

<style lang="scss" scoped></style>
